import { ChevronDown, ChevronRight, LogOut, Menu, User, Settings, Home } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from "@/features/auth/Context/AuthContext";
import { buildMenuTree } from "@/components/utils/menu-builder";
import { useAppSelector } from "@/hooks/use-state";
import { selectAuthLoading, selectMenu, selectUser, selectUserGroup } from "@/features/auth/authSlice";
import { HeaderLinkProps } from "@/components/navigations/navigation.interface";
import { useLocation } from "wouter";
import logoIRB from "@/assets/images/IRB-logo_nameWBCK.png";
import { cn } from "@/lib/utils";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { navigate } from "wouter/use-browser-location";
import { MenuItem } from "@/features/auth/auth.interface";

interface HeaderProps {
  toggleSidebar: () => void;
  title: string;
  isMobile?: boolean;
}

export function Header({ toggleSidebar, isMobile }: HeaderProps) {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("AuthContext not found. Did you forget to wrap your app with <AuthProvider />?");
  const { logout } = auth;
  const user = useAppSelector(selectUser);
  const userGroup = useAppSelector(selectUserGroup);
  const menu = useAppSelector(selectMenu);
  const isLoadingUser = useAppSelector(selectAuthLoading);
  const [location] = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRootRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openByClickLevel2, setOpenByClickLevel2] = useState<number | null>(null);
  const [openByClickLevel3, setOpenByClickLevel3] = useState<number | null>(null);

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
    // Si estamos cerrando el menú, también cerramos los submenús
    if (menuOpen) {
      setOpenByClickLevel2(null);
      setOpenByClickLevel3(null);
    }
  };

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const isMenuButton = menuButtonRef.current?.contains(target);

      if (menuRootRef.current && target && !menuRootRef.current.contains(target) && !isMenuButton) {
        setOpenByClickLevel2(null);
        setOpenByClickLevel3(null);
        setMenuOpen(false); // si querés que el botón "Menú" también se cierre
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [menuRootRef]);



  if (!user || isLoadingUser) return null;

  // Obtener breadcrumbs basado en la ruta actual
  const getBreadcrumbs = () => {
    const breadcrumbs: MenuItem[] = [];
    const currentMenuItem = menu.find((item: MenuItem) => item.path === location);
    
    if (currentMenuItem) {
      // Buscar padres recursivamente
      let currentItem: MenuItem | null = currentMenuItem;
      while (currentItem) {
        breadcrumbs.unshift(currentItem);
        // Buscar el padre solo si parent_id no es null
        currentItem = currentItem.parent_id 
          ? menu.find((item: MenuItem) => item.id === currentItem?.parent_id) || null
          : null;
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  const getGrupoBadge = (grupo: string) => {
    return <Badge className="bg-gray-200 text-gray-800 py-1 hover:bg-gray-800 hover:text-gray-200 mt-1">{grupo}</Badge>
  };

  const handleLogout = async () => {
    await logout();
  };

  const goToInit = () => {
    navigate("/");
  }

  const goToMyAccount = () => {
    navigate("/my-account");
  }

  const menuTree = buildMenuTree(menu);

  const hasBreadcrumbs = breadcrumbs.length > 0;
  return (
    <header className="bg-[var(--background)] text-white shadow-sm h-16 flex items-center px-3 sm:px-6 sticky top-0 z-20 w-full">
      <div className="flex items-center justify-between w-full">
        {!isMobile ? (
          <div className="flex items-center space-x-4">
            {/* MENÚ PRINCIPAL */}
            <div className="relative group">
              {/* BOTÓN MENÚ */}
              <button
                ref={menuButtonRef}
                onClick={toggleMenu}
                className="flex items-center px-4 py-2 text-white hover:bg-[var(--color-secondary)] rounded-md cursor-pointer"
              >
                <Menu size={16} className="mr-1" /> Menú
              </button>
              {/* Contenedores */}
              <div
                ref={menuRootRef}
                className={cn(
                  "absolute left-0 top-full bg-white text-gray-800 shadow-lg rounded-md min-w-[350px] z-50",
                  menuOpen ? "block" : "hidden"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <ul className="py-2">

                  {menuTree.map(level1 => (
                    <li
                      key={level1.id}       
                      className={cn(
                        "relative",
                        openByClickLevel2 === level1.id ? "bg-gray-100" : "hover:bg-gray-100"
                      )}
                    >

                      {/* Nivel 1 */}
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (level1.children.length > 0) {
                            setOpenByClickLevel2(prev => prev === level1.id ? null : level1.id);
                            setOpenByClickLevel3(null);
                          }
                        }}
                        className="px-6 py-2 font-semibold cursor-pointer flex items-center justify-between group-hover/sub2:bg-[var(--color-secondary)] group-hover/sub2:text-white">
                        <span>{level1.nombre}</span>
                        {level1.children.length > 0 && (
                          <ChevronRight size={14} className={cn(
                            "transition-transform duration-200",
                            openByClickLevel2 === level1.id ? "rotate-90" : ""
                          )} />
                        )}
                      </div>

                      {/* Nivel 2 */}
                      {level1.children.length > 0 && (
                        <ul
                          
                          className={cn(
                            "absolute left-full top-0 bg-white shadow-lg rounded-md min-w-[300px] py-2",
                            openByClickLevel2 === level1.id ? "block" : "hidden"
                          )}
                        >

                          {level1.children.map(level2 => (
                            <li
                              key={level2.id}   
                              className={cn(
                                "relative",
                                openByClickLevel3 === level2.id ? "bg-gray-100" : "hover:bg-gray-100"
                              )}
                            >

                              <div 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (level2.children.length > 0) {
                                    setOpenByClickLevel3(prev => prev === level2.id ? null : level2.id);
                                  }
                                }}
                                className="px-6 py-2 cursor-pointer flex items-center justify-between group-hover/sub2:bg-[var(--color-secondary)] group-hover/sub2:text-white">
                                <span>{level2.nombre}</span>
                                {level2.children.length > 0 && (
                                  <ChevronRight size={14} className={cn(
                                    "transition-transform duration-200",
                                    openByClickLevel3 === level2.id ? "rotate-90" : ""
                                  )} />
                                )}
                              </div>

                              {/* Nivel 3 */}
                              {level2.children.length > 0 && (
                                <ul
                                  className={cn(
                                    "absolute left-full top-0 bg-white shadow-lg rounded-md min-w-[280px] py-2",
                                    openByClickLevel3 === level2.id ? "block" : "hidden"
                                  )}
                                  >
                                  {level2.children.map(level3 => (
                                    <li
                                      key={level3.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(level3.path);
                                        setMenuOpen(false);
                                        setOpenByClickLevel2(null);
                                        setOpenByClickLevel3(null);
                                      }}
                                      className="px-6 py-2 hover:bg-[var(--color-secondary)] hover:text-white cursor-pointer text-md"
                                    >
                                      {level3.nombre}
                                    </li>
                                  ))}
                                </ul>
                              )}

                            </li>
                          ))}

                        </ul>
                      )}

                    </li>
                  ))}

                </ul>
              </div>
            </div>

            {/* BREADCRUMBS */}
            <div className="flex items-center text-sm text-gray-300">
              {hasBreadcrumbs && (
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    goToInit();
                  }}
                  className="flex items-center px-2 py-2 mr-4 font-semibold text-white hover:bg-[var(--color-secondary)] rounded-md cursor-pointer"
                  >
                  <Home size={14} className="mx-2" />
                  Inicio
                </button>
              )}

              {hasBreadcrumbs ? (
                breadcrumbs.map((item, index) => (
                  <React.Fragment key={item.id}>

                    <span
                      className={cn(
                        "hover:text-white",
                        index === breadcrumbs.length - 1 && "text-white font-bold"
                      )}
                    >
                      {item.nombre}
                    </span>
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight size={14} className="mx-2" />
                    )}
                  </React.Fragment>
                ))
              ) : (
                <span className="text-gray-400"></span>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-4">
            <button 
              onClick={toggleSidebar}
              className="p-2 rounded-md hover:bg-[var(--color-secondary)] focus:outline-none transition-colors duration-200"
            >
              <Menu size={20} />
            </button>
          </div>
        )}

        {/* LOGO Y USUARIO */}
        {!isMobile && (
          <nav className="flex items-center space-x-3">
            <div className="flex items-center space-x-4 mr-4">
              <div className="flex items-center space-x-2">
                <div className="flex flex-col w-40">
                  <a   
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goToInit();
                    }}
                  >
                    <img src={logoIRB} alt="IRB Gestión" className="hover:bg-white rounded-md cursor-pointer"/>
                  </a>
                </div>
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center p-2 text-white hover:bg-[var(--color-secondary)] rounded-md cursor-pointer">
                <User size={20} />
                <ChevronDown size={16} className="ml-1" />
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="bg-white border rounded-md shadow-lg min-w-[200px] text-center">
                <div className="px-3 py-2 border-b border-gray-100">
                  <div className="w-6 h-6 bg-[var(--color-accent)] rounded-full flex items-center justify-center text-white font-bold text-sm mx-auto mb-2">
                    {user?.name?.charAt(0)}{user?.lastname?.charAt(0)}
                  </div>
                  <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {getGrupoBadge(userGroup?.nombre ?? 'Grupo No Asignado')}
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-2">
                    Zona: {userGroup?.zone_name ?? 'No Asignada'}
                  </div>
                </div>

                <DropdownMenuItem asChild>
                  <button onClick={goToMyAccount} className="w-full flex px-3 py-2 text-sm text-gray-800 hover:!bg-[var(--color-secondary)] hover:!text-white cursor-pointer !rounded-lg">
                    <Settings size={16} className="mr-2" />
                    Mi Cuenta
                  </button>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <button onClick={handleLogout} className="w-full flex px-3 py-2 text-sm text-red-600 hover:!bg-red-600 hover:!text-red-50 cursor-pointer !rounded-lg">
                    <LogOut size={16} className="mr-2" />
                    Cerrar Sesión
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        )}
      </div>
    </header>
  );
}


// Tu componente HeaderLink existente se mantiene igual...
export const HeaderLink = React.forwardRef<
  HTMLAnchorElement,
  HeaderLinkProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    variant?: "default" | "dropdown";
  }
>(({ to, icon, label, active, visible = true, isCompact, className, onClick, variant = "default", ...rest }, ref) => {
  const [, navigate] = useLocation();

  if (!visible) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(to);
    if (onClick) onClick(e);
  };

  const baseClasses = cn(
    "flex items-center transition-colors duration-200 justify-start whitespace-nowrap",
    variant === "dropdown" 
      ? "px-3 py-2 text-gray-700 hover:bg-[var(--color-secondary)] hover:text-white rounded-md text-sm"
      : "px-4 py-2 text-white hover:bg-[var(--color-secondary)] rounded-md text-sm font-medium",
    active && variant === "default" && "bg-[var(--color-secondary)] text-white",
    active && variant === "dropdown" && "bg-[var(--color-secondary)] text-white",
    className
  );

  return (
    <a
      href={to}
      ref={ref}
      onClick={handleClick}
      title={isCompact ? label : undefined}
      className={baseClasses}
      {...rest}
    >
      {icon && <div className="flex-shrink-0 mr-2">{icon}</div>}
      <span className={cn(
        "transition-all duration-300 ease-in-out",
        isCompact ? "opacity-0 scale-95 w-0" : "opacity-100 scale-100 w-auto"
      )}>
        {label}
      </span>
    </a>
  );
});

HeaderLink.displayName = "HeaderLink";